import { useTranslation } from 'react-i18next';
import { Badge } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

/** Visual treatments available for the badge. */
type StoreCountBadgeVariant = 'info' | 'muted';

/** Complete class list per variant — never interpolated, so Tailwind's scanner sees them. */
const VARIANT_CLASSES: Record<StoreCountBadgeVariant, string> = {
  info: 'bg-bg-info text-info hover:bg-bg-info',
  muted: 'bg-muted text-muted-foreground hover:bg-muted',
};

interface StoreCountBadgeProps {
  count: number;
  className?: string;
  variant?: StoreCountBadgeVariant;
}

/**
 * StoreCountBadge - Info badge displaying a pluralized store count ("n sucursales").
 *
 * Shared visual for every place that summarizes how many stores a block or a
 * grouped payment spans (subscription block card, block detail summary,
 * mobile payment cards).
 *
 * @param {StoreCountBadgeProps} props - Component properties
 * @returns {React.ReactElement} The store count badge
 */
export const StoreCountBadge = ({ count, className, variant = 'info' }: StoreCountBadgeProps) => {
  const { t } = useTranslation('subscriptions');

  return (
    <Badge className={cn('w-fit font-sans', VARIANT_CLASSES[variant], className)} variant="secondary">
      {t('card.storeCount', { count })}
    </Badge>
  );
};
